import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import useSelectedTeam from "@/hooks/useSelectedTeam";
import LoadingSpinner from "@/components/LoadingSpinner";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import IntegrationNotEnabled from "../shared/IntegrationNotEnabled";
import ToastDataMapping from "./ToastDataMapping";
import LaunchWizardButton from "@/components/LaunchWizardButton";

const TOAST_COLOR = "#FF4500";

// ─── Exported nav helpers ──────────────────────────────────────────────────────

export function BackToIntegrationLandingToast() {
  const navigate = useNavigate();
  return (
    <button type="button" className="flex cursor-pointer items-center gap-2 text-slate-600"
      onClick={() => navigate("/integrations")}>
      <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
        <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
      </svg>
      Back to Integrations
    </button>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface ToastConfig {
  id: number;
  siteId: number;
  displayName: string;
  locationName: string;
  restaurantGuid: string;
  enabled: boolean;
  createdAt: string;
}

type TabId = "overview" | "accounts" | "ai-tools" | "data-mapping" | "logs";

// ─── Capabilities ─────────────────────────────────────────────────────────────

const CAPABILITIES = [
  {
    title: "Menu intelligence",
    description: "Answer 'do you have X?', dietary restrictions, prices, and today's specials in real time"
  },
  {
    title: "Order status",
    description: "Customers ask 'is my order ready?' and get live status without calling the restaurant"
  },
  {
    title: "Table availability",
    description: "Check floor plan and table status for walk-in and reservation conversations"
  },
  {
    title: "Restaurant information",
    description: "Hours, location, parking, dining options, and contact info always on hand"
  },
  {
    title: "Sales reporting",
    description: "Daily revenue, top items, and check averages for management conversations"
  }
];

// ─── AI Tools ─────────────────────────────────────────────────────────────────

const AI_TOOLS = [
  { name: "toast_get_menu_groups", label: "Get menu groups", group: "Menu", description: "Retrieve all menu categories/groups from Toast (e.g., Appetizers, Mains, Desserts) with item counts." },
  { name: "toast_search_menu", label: "Search menu", group: "Menu", description: "Search menu items by name, keyword, or dietary term. Returns matching items with price and description." },
  { name: "toast_get_menu_item", label: "Get menu item", group: "Menu", description: "Get details for a specific menu item by name — price, description, calories, and image." },
  { name: "toast_get_allergen_info", label: "Get allergen info", group: "Menu", description: "Get allergen and ingredient information for a menu item. Always advise confirming with staff." },
  { name: "toast_get_specials", label: "Get specials", group: "Menu", description: "Get current daily specials, featured items, and seasonal offerings from Toast." },
  { name: "toast_get_modifiers", label: "Get item modifiers", group: "Menu", description: "Get customization options for a menu item — sizes, add-ons, toppings, and any additional pricing." },
  { name: "toast_get_items_by_tag", label: "Get items by dietary tag", group: "Menu", description: "Find items matching a dietary preference — vegan, gluten-free, vegetarian, dairy-free, etc." },
  { name: "toast_get_order", label: "Get order", group: "Orders", description: "Get full details for an order by ID — items, quantities, status, and timestamps." },
  { name: "toast_get_table_order", label: "Get table order", group: "Orders", description: "Look up the active order for a specific table number." },
  { name: "toast_get_restaurant_info", label: "Get restaurant info", group: "Restaurant Info", description: "General restaurant info — name, address, phone, and contact details." },
  { name: "toast_get_hours", label: "Get hours", group: "Restaurant Info", description: "Current operating hours for all days of the week from Toast." },
  { name: "toast_get_restaurant_services", label: "Get services", group: "Restaurant Info", description: "Check which ordering modes are enabled — dine-in, takeout, delivery, online ordering." },
  { name: "toast_get_discounts", label: "Get discounts", group: "Restaurant Info", description: "Get active discounts and promotions configured in Toast." },
  { name: "toast_get_shift_info", label: "Get shift info", group: "Restaurant Info", description: "Get current shift info — employees clocked in. Requires Toast labor API permissions." },
];

// ─── Create Form ──────────────────────────────────────────────────────────────

interface CreateFormProps {
  onSuccess(): void;
  onCancel(): void;
}

function CreateForm({ onSuccess, onCancel }: CreateFormProps) {
  const api = useApi();
  const [form, setForm] = useState({ displayName: "", clientId: "", clientSecret: "", restaurantGuid: "", locationName: "" });
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<{ valid: boolean; locationName?: string; error?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleTest() {
    if (!form.clientId || !form.clientSecret || !form.restaurantGuid) { setError("Client ID, Client Secret, and Restaurant GUID are required."); return; }
    setError(null); setTesting(true); setTestResult(null);
    try {
      const resp = await api.admin.post("Toast/Test", { clientId: form.clientId.trim(), clientSecret: form.clientSecret.trim(), restaurantGuid: form.restaurantGuid.trim() });
      const data = await resp.json();
      setTestResult(data);
      if (data.valid && data.locationName) setForm(f => ({ ...f, locationName: data.locationName, displayName: f.displayName || data.locationName }));
    } catch (err) {
      console.error("Toast test failed:", err);
      setTestResult({ valid: false, error: "Connection test failed." });
    } finally { setTesting(false); }
  }

  async function handleSave() {
    if (!form.clientId || !form.clientSecret || !form.restaurantGuid) { setError("All required fields must be filled."); return; }
    setError(null); setSaving(true);
    try {
      const resp = await api.admin.post("Toast/Configs", { ...form });
      if (!resp.ok) { const d = await resp.json(); setError(d?.error ?? "Save failed."); return; }
      onSuccess();
    } catch (err) {
      console.error("Toast save failed:", err);
      setError("Failed to save. Please try again.");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-semibold text-slate-800 text-lg">Connect Toast POS</div>
          <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-600">
            <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-4">Find these in Toast Web → Integrations → Toast API. Create credentials to get your Client ID and Secret. Your Restaurant GUID is in Toast Web → Restaurant Info.</p>
        {error && <div className="mb-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs p-3">{error}</div>}
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">Display Name</label>
            <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
              placeholder="e.g. Downtown Location" value={form.displayName} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">Client ID <span className="text-red-500">*</span></label>
            <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-300"
              placeholder="Toast Client ID" value={form.clientId} onChange={e => setForm(f => ({ ...f, clientId: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">Client Secret <span className="text-red-500">*</span></label>
            <input type="password" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-300"
              placeholder="Toast Client Secret" value={form.clientSecret} onChange={e => setForm(f => ({ ...f, clientSecret: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">Restaurant GUID <span className="text-red-500">*</span></label>
            <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-300"
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" value={form.restaurantGuid} onChange={e => setForm(f => ({ ...f, restaurantGuid: e.target.value }))} />
          </div>
        </div>
        {testResult && (
          <div className={`mt-3 rounded-lg p-3 text-xs ${testResult.valid ? "bg-green-50 border border-green-200 text-green-700" : "bg-red-50 border border-red-200 text-red-700"}`}>
            {testResult.valid ? `Connected — ${testResult.locationName || "Restaurant verified"}` : (testResult.error ?? "Connection failed.")}
          </div>
        )}
        <div className="mt-4 flex items-center gap-3">
          <button type="button" onClick={handleTest} disabled={testing}
            className="px-3 py-2 rounded-lg text-xs font-medium border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            {testing ? "Testing..." : "Test Connection"}
          </button>
          <button type="button" onClick={handleSave} disabled={saving}
            className="flex-1 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50"
            style={{ backgroundColor: TOAST_COLOR }}>
            {saving ? "Saving..." : "Save Connection"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Shared UI Components ─────────────────────────────────────────────────────

function CapabilitiesPanel() {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-2xl shadow-md mb-4">
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 rounded-2xl" aria-expanded={open}>
        <div className="flex items-center gap-3">
          <span className="font-semibold text-slate-800">What's included</span>
          <span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-0.5 rounded-full">{CAPABILITIES.length} feature areas</span>
        </div>
        <svg className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`} viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 10.5L3 5.5H13L8 10.5Z" />
        </svg>
      </button>
      {open && (
        <div className="border-t border-slate-100 px-6 pb-6">
          <div className="mt-4 rounded-lg bg-green-50 border border-green-200 p-3 mb-4 text-xs text-green-800">
            <strong>AI tools work without any configuration.</strong> Connect Toast (API credentials) and the AI gains access to menus, orders, and restaurant data in real time.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {CAPABILITIES.map((cap, i) => (
              <div key={i} className="rounded-lg border border-slate-100 bg-slate-50 p-4">
                <div className="text-sm font-medium text-slate-700 mb-0.5">{cap.title}</div>
                <div className="text-xs text-slate-500">{cap.description}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function HowItWorksCard({ onConnect }: { onConnect(): void }) {
  return (
    <div className="bg-white rounded-2xl shadow-md p-6 mb-4">
      <div className="font-semibold text-slate-800 text-base mb-1">How Toast works in Velaro</div>
      <p className="text-xs text-slate-500 mb-4">Connect once — every menu item, order, and restaurant detail becomes AI-searchable instantly.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0" style={{ backgroundColor: TOAST_COLOR }}>1</span>
            <span className="font-semibold text-slate-700 text-sm">Connect Toast</span>
          </div>
          <p className="text-xs text-slate-500 mb-3">In Toast Web → Integrations → Toast API → create API credentials → copy Client ID and Secret → find Restaurant GUID in restaurant info.</p>
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span className="font-mono bg-white border border-slate-200 px-1.5 py-0.5 rounded text-[10px]">Client ID + Secret</span>
          </div>
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-slate-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none"><path d="M3 2L7 5L3 8" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </div>
        </div>
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
            <span className="font-semibold text-green-800 text-sm">8 AI tools activate</span>
          </div>
          <p className="text-xs text-green-700 mb-3">Every menu, order, and restaurant detail is instantly searchable — no extra setup.</p>
          <div className="grid grid-cols-2 gap-1">
            {["Menu categories", "Menu search", "Item details", "Allergen info", "Daily specials", "Order lookup", "Restaurant info", "Hours & schedule"].map((t) => (
              <div key={t} className="flex items-center gap-1 text-[10px] text-green-700">
                <svg viewBox="0 0 8 8" width="8" height="8" fill="none" className="flex-shrink-0"><path d="M1 4L3 6L7 2" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round" /></svg>
                {t}
              </div>
            ))}
          </div>
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-slate-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none"><path d="M3 2L7 5L3 8" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </div>
        </div>
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
            <span className="font-semibold text-blue-800 text-sm">Scale 24/7 guest support</span>
          </div>
          <p className="text-xs text-blue-700 mb-3">Answer menu questions, order status, and hours instantly — without tying up staff.</p>
          <div className="space-y-2">
            {[
              ["Menu Q&A bot", "Answers dietary, allergy, and pricing questions 24/7"],
              ["Order tracker", "Customers check status without calling"],
              ["Restaurant concierge", "Hours, location, parking, and dining options on demand"]
            ].map(([title, desc]) => (
              <div key={title} className="bg-white rounded-lg p-2 border border-blue-100">
                <div className="text-[10px] font-semibold text-blue-800 uppercase tracking-wide">{title}</div>
                <div className="text-[10px] text-blue-600 mt-0.5">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function OverviewTab({ hasConnection, onConnect, onOpenWizard }: { hasConnection: boolean; onConnect(): void; onOpenWizard(): void }) {
  return (
    <div>
      <HowItWorksCard onConnect={onConnect} />
      {!hasConnection ? (
        <div className="bg-white rounded-2xl shadow-md p-6 mb-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-semibold text-slate-800 mb-1">Connect your Toast account</div>
            <p className="text-sm text-slate-500">Enter your API credentials and your menus, orders, and restaurant data become instantly searchable by the AI during any conversation.</p>
          </div>
          <button type="button" onClick={onConnect} className="shrink-0 px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: TOAST_COLOR }}>
            Connect Now →
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-md p-6 mb-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-semibold text-slate-800 mb-1">Launch Starter Workflow Wizard</div>
            <p className="text-sm text-slate-500">Build a Menu Q&A Bot, Order Status Bot, or Restaurant Concierge in minutes using a pre-built Toast template.</p>
          </div>
          <button type="button" onClick={onOpenWizard} className="shrink-0 px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: TOAST_COLOR }}>
            Launch Wizard →
          </button>
        </div>
      )}
    </div>
  );
}

function AiToolsTab({ hasConnection, onOpenWizard }: { hasConnection: boolean; onOpenWizard(): void }) {
  const groups = [...new Set(AI_TOOLS.map((t) => t.group))];
  return (
    <div className="bg-white rounded-2xl shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-semibold text-slate-800 text-lg flex items-center gap-2">
            AI Tools
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${hasConnection ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
              {hasConnection ? `${AI_TOOLS.length} active` : "No connection"}
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-1">All tools activate automatically on connect — no configuration required.</p>
        </div>
        {hasConnection && (
          <LaunchWizardButton onClick={onOpenWizard} />
        )}
      </div>
      <div className="space-y-6">
        {groups.map((group) => (
          <div key={group}>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{group}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {AI_TOOLS.filter((t) => t.group === group).map((tool) => (
                <div key={tool.name} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: TOAST_COLOR }} />
                    <div>
                      <div className="text-xs font-semibold text-slate-700">{tool.label}</div>
                      <div className="text-[10px] font-mono text-slate-400 mb-1">{tool.name}</div>
                      <div className="text-xs text-slate-500">{tool.description}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AccountsTab({ configs, onRefresh, onAdd }: { configs: ToastConfig[]; onRefresh(): void; onAdd(): void }) {
  const api = useApi();
  const [deleting, setDeleting] = useState<number | null>(null);
  const [deletingConfirm, setDeletingConfirm] = useState<number | null>(null);

  async function handleDelete(id: number) {
    setDeleting(id);
    try {
      await api.admin.delete(`Toast/Configs/${id}`);
      onRefresh();
    } catch (err) {
      console.error("Toast: failed to delete config:", err);
    } finally {
      setDeleting(null);
      setDeletingConfirm(null);
    }
  }

  return (
    <div className="bg-white rounded-2xl shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="font-semibold text-slate-800 text-lg">Connected Accounts</div>
        <button type="button" onClick={onAdd} className="px-3 py-1.5 rounded-lg text-xs font-medium text-white" style={{ backgroundColor: TOAST_COLOR }}>
          + Add Account
        </button>
      </div>
      {configs.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <div className="font-medium text-slate-600 mb-1">No Toast account connected</div>
          <p className="text-sm">Connect your Toast API credentials to enable AI-powered restaurant support for your guests.</p>
          <button type="button" onClick={onAdd} className="mt-4 px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: TOAST_COLOR }}>
            Connect Toast →
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {configs.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 p-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-lg font-bold" style={{ backgroundColor: TOAST_COLOR }}>T</div>
                <div>
                  <div className="font-medium text-slate-800">{c.displayName}</div>
                  {c.locationName && <div className="text-xs text-slate-500">{c.locationName}</div>}
                  <div className="text-xs text-slate-400 font-mono">GUID: ****{c.restaurantGuid?.slice(-8)}</div>
                  <div className="text-xs text-slate-400">{new Date(c.createdAt).toLocaleDateString()}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.enabled ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                  {c.enabled ? "Active" : "Disabled"}
                </span>
                {deletingConfirm === c.id ? (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-slate-600">Remove?</span>
                    <button type="button" onClick={() => handleDelete(c.id)} disabled={deleting === c.id}
                      className="text-red-600 font-medium hover:text-red-800">
                      {deleting === c.id ? "Removing..." : "Yes"}
                    </button>
                    <button type="button" onClick={() => setDeletingConfirm(null)} className="text-slate-500 hover:text-slate-700">Cancel</button>
                  </div>
                ) : (
                  <button type="button" onClick={() => setDeletingConfirm(c.id)} className="text-xs text-red-500 hover:text-red-700 transition-colors">Remove</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function Toast() {
  const api = useApi();
  const navigate = useNavigate();
  const [configs, setConfigs] = useState<ToastConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [showWizard, setShowWizard] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState<number>(0);
  const { teamId } = useSelectedTeam();

  const fetchConfigs = useCallback(async () => {
    try {
      const resp = await api.admin.get("Toast/Configs");
      if (resp.status === 402) { setEnabled(false); return; }
      const data = await resp.json();
      setConfigs(Array.isArray(data) ? data : []);
    } catch {
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  useEffect(() => {
    if (configs.length > 0 && selectedConfigId === 0) {
      setSelectedConfigId(configs.find((c) => c.enabled)?.id ?? configs[0].id);
    }
  }, [configs, selectedConfigId]);

  if (!enabled) {
    return <IntegrationNotEnabled name="Toast Integration" description="Toast integration requires an upgrade. Contact Velaro support to enable it for your account." />;
  }

  const hasActiveConfig = configs.some((c) => c.enabled);

  const TABS: { id: TabId; label: string }[] = [
    { id: "overview", label: "Overview" },
    { id: "accounts", label: "Accounts" },
    { id: "ai-tools", label: "AI Tools" },
    { id: "data-mapping", label: "Data Mapping" },
    { id: "logs", label: "Activity Log" }
  ];

  return (
    <div>
      {showWizard && (
        <StarterWorkflowWizard integration="toast" onClose={() => setShowWizard(false)} />
      )}
      {showForm && (
        <CreateForm
          onSuccess={() => { setShowForm(false); fetchConfigs(); setActiveTab("accounts"); }}
          onCancel={() => setShowForm(false)}
        />
      )}
      <CapabilitiesPanel />
      <div className="flex border-b border-slate-200 mb-4">
        {TABS.map((tab) => (
          <button key={tab.id} type="button"
            className={`py-2 mr-4 text-sm cursor-pointer ${activeTab === tab.id ? "border-b-2 border-primary text-primary font-medium" : "text-slate-500"}`}
            style={activeTab === tab.id ? { borderColor: TOAST_COLOR, color: TOAST_COLOR } : {}}
            onClick={() => setActiveTab(tab.id)}>
            {tab.label}
            {tab.id === "accounts" && configs.length > 0 && (
              <span className="ml-1.5 text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{configs.length}</span>
            )}
          </button>
        ))}
      </div>

      {hasActiveConfig && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-blue-800 mb-0.5">
                Deploy the chat widget to your site
              </div>
              <div className="text-xs text-blue-600">
                Your Toast integration is connected — grab your embed code from Deployments to go live.
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <a href="/chat_window" className="text-xs px-3 py-1.5 rounded-lg bg-white border border-blue-300 text-blue-700 hover:bg-blue-100 transition">Chat Window</a>
              <a href="/deployments" className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition">Get Embed Code →</a>
            </div>
          </div>
          <div className="flex items-start gap-2 bg-white/60 rounded-lg px-3 py-2 text-xs text-blue-700 border border-blue-100">
            <i className="fa-solid fa-lightbulb mt-0.5 shrink-0" />
            <span>
              <strong>Tip:</strong> Use <strong>Page Widgets</strong> from{" "}
              <a href="/deployments" className="underline hover:text-blue-900">Deployments → Page Widgets</a> to add a
              lightweight menu Q&A widget on your ordering page — no live agent required.
            </span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : (
        <>
          {activeTab === "overview" && (
            <OverviewTab hasConnection={hasActiveConfig}
              onConnect={() => setShowForm(true)}
              onOpenWizard={() => setShowWizard(true)} />
          )}
          {activeTab === "accounts" && (
            <AccountsTab configs={configs} onRefresh={fetchConfigs} onAdd={() => setShowForm(true)} />
          )}
          {activeTab === "ai-tools" && <AiToolsTab hasConnection={hasActiveConfig} onOpenWizard={() => setShowWizard(true)} />}
          {activeTab === "data-mapping" && (
            <div className="bg-white rounded-2xl shadow-md p-6">
              <div className="font-semibold text-slate-800 text-lg mb-1">Data Mapping — Toast</div>
              <ToastDataMapping
                teamId={teamId ?? 0}
                selectedConfigId={selectedConfigId}
              />
            </div>
          )}
          {activeTab === "logs" && (
            <div className="bg-white rounded-2xl shadow-md p-6">
              <IntegrationActivityLog integrationSlug="Toast" />
            </div>
          )}
        </>
      )}
    </div>
  );
}
